import {
    $applyNodeReplacement,
    $generateHtmlFromNodes,
    $generateNodesFromDOM,
    $getRoot,
    EditorConfig,
    editorTheme,
    HeadingNode,
    HistoryPlugin,
    LexicalComposer,
    LexicalEditor,
    LexicalErrorBoundary,
    LinkNode,
    LinkPlugin,
    ListItemNode,
    ListNode,
    ListPlugin,
    OnChangePlugin,
    ParagraphNode,
    RichContentEditable,
    RichTextPlugin,
    ToolbarPlugin,
    ToolbarSeparator,
} from '@talentmesh/rte';
import { FieldError } from '@talentmesh/ui/components/field';
import { cn } from '@talentmesh/ui/lib/utils';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useDebounceCallback } from 'usehooks-ts';
import PlaceholderPlugins, { Placeholder } from '../../../components/RTE/PlaceholderPlugins';
import { useEmailCandidateFormContext } from '../useEmailCandidateForm';
import TMWingsAnimation from '@/Components/Animations/TMWingsAnimation/TMWingsAnimation';
import TypingTextAnimation from '@/Components/Animations/TypingTextAnimation';
import EmailTemplatePlaceholders from '@/Pages/AccountSettings/Components/EmailTemplatePlaceholders';
import UIStrings from '@/Utils/UIStrings';

const placeholders: Placeholder[] = [
    { label: UIStrings.Name, value: EmailTemplatePlaceholders.ApplicantName },
    { label: UIStrings.Signature, value: EmailTemplatePlaceholders.Signature },
];

class EmailParagraphNode extends ParagraphNode {
    $config() {
        return this.config('outlook-paragraph', { extends: ParagraphNode });
    }

    createDOM(config: EditorConfig): HTMLElement {
        const element = super.createDOM(config);
        element.style.margin = '0';
        return element;
    }
}

function $createCustomEmailParagraphNode() {
    return $applyNodeReplacement(new EmailParagraphNode());
}

const EmailCandidateEditor = () => {
    const form = useEmailCandidateFormContext();
    const { isLoading, defaultValues } = form.formState;
    const hasLengthError = !!form.formState.errors.emailHtmlTextLength?.message;

    const $prepopulatedRichText = useCallback(
        (editor: LexicalEditor) => {
            if (!defaultValues?.emailHtml) {
                return;
            }

            const parser = new DOMParser();
            const dom = parser.parseFromString(defaultValues.emailHtml, 'text/html');
            const root = $getRoot();
            const nodes = $generateNodesFromDOM(editor, dom);

            root.append(...nodes);
        },
        [defaultValues],
    );

    const handleEditorOnChange = useDebounceCallback<Parameters<typeof OnChangePlugin>['0']['onChange']>(
        (editorState, editor) => {
            editorState.read(() => {
                const length = editor.getRootElement()?.textContent?.length || 0;
                const value = $generateHtmlFromNodes(editor);
                form.setValue('emailHtml', value, { shouldDirty: true, shouldValidate: true });
                form.setValue('emailHtmlTextLength', length, { shouldDirty: true, shouldValidate: true });
            });
        },
        10,
    );

    return (
        <div className="flex h-115 flex-col rounded-lg">
            {isLoading ? (
                <div className="border-input flex h-full w-full flex-col items-center justify-center gap-4 rounded-md border">
                    <h2 className="text-2xl font-semibold">{UIStrings.DraftingYourMessage}</h2>
                    <TMWingsAnimation className="h-42" />
                    <TypingTextAnimation
                        deletionSpeed={20}
                        texts={[UIStrings.AIIsPreparingTheFirstDraft, UIStrings.DraftingYourEmail]}
                    />
                </div>
            ) : (
                <div className="flex h-115 max-h-115 flex-1 flex-col" data-cy="outreachEmailRichTextEditor">
                    <LexicalComposer
                        initialConfig={{
                            editorState: $prepopulatedRichText,
                            namespace: 'Outreach candidate email editor',
                            onError: ({ message }) => toast.error(message),
                            theme: editorTheme,
                            nodes: [
                                LinkNode,
                                HeadingNode,
                                ListNode,
                                ListItemNode,
                                EmailParagraphNode,
                                {
                                    replace: ParagraphNode,
                                    with: () => $createCustomEmailParagraphNode(),
                                    withKlass: EmailParagraphNode,
                                },
                            ],
                        }}
                    >
                        <HistoryPlugin />
                        <ToolbarPlugin features={['headingStyle', 'bold', 'italic', 'ol', 'ul']}>
                            <ToolbarSeparator />
                            <div className="ml-2 flex gap-2">
                                <PlaceholderPlugins placeholders={placeholders} />
                            </div>
                        </ToolbarPlugin>
                        <OnChangePlugin
                            ignoreHistoryMergeTagChange
                            ignoreSelectionChange
                            onChange={handleEditorOnChange}
                        />
                        <ListPlugin />
                        <LinkPlugin />
                        <RichTextPlugin
                            ErrorBoundary={LexicalErrorBoundary}
                            contentEditable={
                                <RichContentEditable
                                    className={cn(
                                        'border-border h-full border',
                                        hasLengthError ? 'border-destructive' : '',
                                    )}
                                />
                            }
                        />
                    </LexicalComposer>
                </div>
            )}
            <FieldError className="mt-1">{form.formState.errors.emailHtmlTextLength?.message}</FieldError>
        </div>
    );
};

export default EmailCandidateEditor;
